import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config.js';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { format, parseISO, isValid, differenceInBusinessDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { updatePTOForAbsenceChange } from '../services/ptoService';

const SupervisorDashboard = ({ currentUser }) => {
    const [pendingRequests, setPendingRequests] = useState([]);
    const [teamAbsences, setTeamAbsences] = useState([]);
    const [deniedRequests, setDeniedRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [ptoOverrides, setPtoOverrides] = useState({});
    const [teamPTOBalances, setTeamPTOBalances] = useState([]);
    const [isLoadingPTO, setIsLoadingPTO] = useState(false);
    
    // NEW: Direct Reports State
    const [teamMembers, setTeamMembers] = useState([]);
    
    // Statistics Time Frame States
    const getDefaultSchoolYearStart = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const startYear = currentMonth < 7 ? currentYear - 1 : currentYear;
        return format(new Date(startYear, 7, 1), 'yyyy-MM-dd');
    };
    
    const getDefaultSchoolYearEnd = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        const endYear = currentMonth < 7 ? currentYear : currentYear + 1;
        return format(new Date(endYear, 5, 30), 'yyyy-MM-dd');
    };

    const [statsStartDate, setStatsStartDate] = useState(getDefaultSchoolYearStart());
    const [statsEndDate, setStatsEndDate] = useState(getDefaultSchoolYearEnd());
    const [offCampusRequests, setOffCampusRequests] = useState([]);
    const [tenantSettings, setTenantSettings] = useState(null);
    
    // Campus Attendance States
    const [showAttendance, setShowAttendance] = useState(false);
    const [attendanceDate, setAttendanceDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [attendanceTime, setAttendanceTime] = useState(format(new Date(), 'HH:mm'));
    const [allFaculty, setAllFaculty] = useState([]);
    const [absentFaculty, setAbsentFaculty] = useState([]);
    const [offCampusFaculty, setOffCampusFaculty] = useState([]);
    const [presentFaculty, setPresentFaculty] = useState([]);
    const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);
    const [showFullRoster, setShowFullRoster] = useState(false);
    
    // Modal states for clickable cards
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalData, setModalData] = useState([]);

    // Styles object
    const styles = {
        dashboardContainer: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            minHeight: '100vh'
        },
        dashboardHeader: {
            textAlign: 'center',
            marginBottom: '30px',
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        },
        attendanceHeader: {
            textAlign: 'center',
            marginBottom: '30px',
            padding: '20px',
            background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
            color: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        },
        h1: {
            margin: 0,
            fontSize: '2.5rem',
            fontWeight: '700'
        },
        dashboardSubtitle: {
            margin: '10px 0 0 0',
            opacity: 0.9,
            fontSize: '1.1rem'
        },
        actionBar: {
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '30px',
            gap: '15px'
        },
        btnAttendance: {
            background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)',
            color: 'white',
            fontSize: '1.1rem',
            padding: '12px 24px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
        },
        dashboardCard: {
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            marginBottom: '30px',
            overflow: 'hidden',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 25px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e9ecef'
        },
        h2: {
            margin: 0,
            color: '#2c3e50',
            fontSize: '1.5rem',
            fontWeight: '600'
        },
        badge: {
            padding: '6px 12px',
            borderRadius: '20px',
            fontSize: '0.8rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        badgeWarning: {
            backgroundColor: '#ffc107',
            color: '#212529'
        },
        badgeInfo: {
            backgroundColor: '#17a2b8',
            color: 'white'
        },
        badgeDanger: {
            backgroundColor: '#dc3545',
            color: 'white'
        },
        badgeSecondary: {
            backgroundColor: '#6c757d',
            color: 'white'
        },
        badgeSuccess: {
            backgroundColor: '#28a745',
            color: 'white'
        },
        badgePrimary: {
            backgroundColor: '#007bff',
            color: 'white'
        },
        controlPanel: {
            padding: '25px'
        },
        dateTimeControls: {
            display: 'flex',
            gap: '30px',
            alignItems: 'flex-end',
            justifyContent: 'center',
            flexWrap: 'wrap'
        },
        controlGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
        },
        controlLabel: {
            fontWeight: '600',
            color: '#495057',
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        dateTimeInput: {
            padding: '10px 16px',
            border: '2px solid #e9ecef',
            borderRadius: '8px',
            fontSize: '1rem',
            transition: 'all 0.2s ease',
            backgroundColor: '#f8f9fa',
            minWidth: '180px'
        },
        btn: {
            border: 'none',
            borderRadius: '6px',
            padding: '8px 16px',
            fontSize: '0.85rem',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        btnPrimary: {
            backgroundColor: '#007bff',
            color: 'white'
        },
        btnSecondary: {
            backgroundColor: '#6c757d',
            color: 'white'
        },
        btnSm: {
            padding: '6px 12px',
            fontSize: '0.8rem'
        },
        attendanceSummaryContainer: {
            padding: '30px'
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '25px'
        },
        statCard: {
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: '12px',
            padding: '30px 20px',
            textAlign: 'center',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.3s ease',
            border: '1px solid #e9ecef',
            cursor: 'pointer'
        },
        statIcon: {
            fontSize: '3rem',
            marginBottom: '15px',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
        },
        statNumber: {
            fontSize: '3.5rem',
            fontWeight: '700',
            margin: '10px 0',
            lineHeight: 1
        },
        statLabel: {
            fontSize: '0.9rem',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            color: '#6c757d',
            fontWeight: '600',
            marginTop: '5px'
        },
        presentCard: {
            background: 'linear-gradient(135deg, #d4edda 0%, #ffffff 100%)',
            borderColor: '#c3e6cb'
        },
        presentCardNumber: {
            color: '#155724'
        },
        absentCard: {
            background: 'linear-gradient(135deg, #f8d7da 0%, #ffffff 100%)',
            borderColor: '#f5c6cb'
        },
        absentCardNumber: {
            color: '#721c24'
        },
        offcampusCard: {
            background: 'linear-gradient(135deg, #fff3cd 0%, #ffffff 100%)',
            borderColor: '#ffeaa7'
        },
        offcampusCardNumber: {
            color: '#856404'
        },
        totalCard: {
            background: 'linear-gradient(135deg, #d1ecf1 0%, #ffffff 100%)',
            borderColor: '#bee5eb'
        },
        totalCardNumber: {
            color: '#0c5460'
        },
        tableContainer: {
            overflowX: 'auto'
        },
        modernTable: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.95rem'
        },
        th: {
            backgroundColor: '#f8f9fa',
            color: '#495057',
            fontWeight: '600',
            textAlign: 'left',
            padding: '15px',
            borderBottom: '2px solid #dee2e6',
            textTransform: 'uppercase',
            fontSize: '0.8rem',
            letterSpacing: '0.5px'
        },
        td: {
            padding: '15px',
            borderBottom: '1px solid #f1f3f4',
            verticalAlign: 'top'
        },
        employeeCell: {
            color: '#2c3e50',
            fontWeight: '600'
        },
        reasonTag: {
            display: 'inline-block',
            backgroundColor: '#f3e5f5',
            color: '#7b1fa2',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '0.85rem',
            fontWeight: '500'
        },
        typeBadge: {
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '0.8rem',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            backgroundColor: '#ffe0e0',
            color: '#dc3545'
        },
        emptyState: {
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6c757d'
        },
        emptyIcon: {
            fontSize: '4rem',
            marginBottom: '20px'
        },
        emptyStateH3: {
            margin: '0 0 10px 0',
            color: '#495057',
            fontWeight: '600'
        },
        emptyStateP: {
            margin: 0,
            fontSize: '1.1rem'
        },
        dateDisplay: {
            display: 'inline-block',
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '0.85rem',
            fontWeight: '500'
        },
        notesCell: {
            maxWidth: '200px',
            wordWrap: 'break-word'
        },
        actionButtons: {
            display: 'flex',
            gap: '8px'
        },
        btnSuccess: {
            backgroundColor: '#28a745',
            color: 'white'
        },
        btnDanger: {
            backgroundColor: '#dc3545',
            color: 'white'
        },
        statsCell: {
            textAlign: 'center'
        },
        totalDays: {
            display: 'block',
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#007bff'
        },
        totalDaysSmall: {
            color: '#6c757d',
            textTransform: 'uppercase',
            fontSize: '0.7rem',
            letterSpacing: '0.5px'
        },
        breakdownCell: {
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
        },
        reasonBreakdown: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px'
        },
        reasonLabel: {
            fontWeight: '500',
            color: '#495057'
        },
        reasonCount: {
            fontWeight: '600',
            color: '#007bff'
        },
        deniedRow: {
            backgroundColor: '#fff5f5'
        },
        denialReason: {
            color: '#dc3545',
            fontStyle: 'italic'
        },
        loadingSpinner: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            textAlign: 'center'
        },
        spinner: {
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
        },
        errorMessage: {
            textAlign: 'center',
            padding: '40px',
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
            color: '#dc3545'
        },
        offCampusList: {
            marginTop: '10px',
            fontSize: '0.85rem',
            maxHeight: '200px',
            overflowY: 'auto'
        },
        offCampusItem: {
            padding: '8px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            marginBottom: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        offCampusDate: {
            fontWeight: '600',
            color: '#007bff'
        },
        offCampusTime: {
            color: '#6c757d',
            fontSize: '0.8rem'
        },
        offCampusReason: {
            fontStyle: 'italic',
            color: '#495057',
            fontSize: '0.85rem'
        },
        statsTimeFrame: {
            backgroundColor: '#e9ecef',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '20px',
            flexWrap: 'wrap'
        },
        rosterToggle: {
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '8px 16px',
            cursor: 'pointer',
            fontSize: '14px',
            marginLeft: '15px',
            transition: 'background-color 0.3s ease'
        },
        detailModal: {
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '30px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            zIndex: 1000
        },
        modalOverlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999
        },
        modalHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            paddingBottom: '15px',
            borderBottom: '2px solid #e9ecef'
        },
        modalTitle: {
            fontSize: '24px',
            fontWeight: '600',
            color: '#212529',
            margin: 0
        },
        closeModalButton: {
            background: 'none',
            border: 'none',
            fontSize: '28px',
            cursor: 'pointer',
            color: '#6c757d',
            padding: '0',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease'
        },
        facultyListItem: {
            padding: '12px 0',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        facultyName: {
            fontWeight: '500',
            color: '#212529'
        },
        facultyEmail: {
            color: '#6c757d',
            fontSize: '14px'
        },
        noDataMessage: {
            textAlign: 'center',
            color: '#6c757d',
            padding: '40px 20px',
            fontSize: '16px'
        }
    };

    // Export functions
    const exportToCSV = () => {
        if (!modalData || modalData.length === 0) return;
        
        const headers = ['Name', 'Email', 'Department', 'Role'];
        const rows = modalData.map(faculty => [
            faculty.name || faculty.displayName || 'N/A',
            faculty.email || 'N/A',
            faculty.department || 'N/A',
            faculty.role || 'N/A'
        ]);
        
        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `${modalTitle.replace(/ /g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const printReport = () => {
        const printWindow = window.open('', '_blank');
        const printContent = `
            <html>
                <head>
                    <title>${modalTitle} - ${format(new Date(), 'MM/dd/yyyy')}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        h1 { color: #212529; border-bottom: 2px solid #dee2e6; padding-bottom: 10px; }
                        .report-info { margin-bottom: 20px; color: #6c757d; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                        th, td { border: 1px solid #dee2e6; padding: 10px; text-align: left; }
                        th { background-color: #f8f9fa; font-weight: bold; }
                        tr:nth-child(even) { background-color: #f8f9fa; }
                    </style>
                </head>
                <body>
                    <h1>${modalTitle}</h1>
                    <div class="report-info">
                        <p><strong>Generated on:</strong> ${format(new Date(), 'MMMM dd, yyyy \'at\' h:mm a')}</p>
                        <p><strong>Attendance Date:</strong> ${format(parseISO(attendanceDate), 'MMMM dd, yyyy')}</p>
                        <p><strong>Attendance Time:</strong> ${attendanceTime}</p>
                        <p><strong>Total Count:</strong> ${modalData.length}</p>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Department</th>
                                <th>Role</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${modalData.map((faculty, index) => `
                                <tr>
                                    <td>${index + 1}</td>
                                    <td>${faculty.name || faculty.displayName || 'N/A'}</td>
                                    <td>${faculty.email || 'N/A'}</td>
                                    <td>${faculty.department || 'N/A'}</td>
                                    <td>${faculty.role || 'N/A'}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </body>
            </html>
        `;
        
        printWindow.document.write(printContent);
        printWindow.document.close();
        setTimeout(() => { printWindow.print(); }, 250);
    };

    // Click handlers for stat cards
    const handleCardClick = (type) => {
        let title, data;
        switch(type) {
            case 'present':
                title = 'Faculty On Campus';
                data = presentFaculty;
                break;
            case 'absent':
                title = 'Faculty Absent';
                data = absentFaculty;
                break;
            case 'offcampus':
                title = 'Faculty Off-Campus';
                data = offCampusFaculty;
                break;
            case 'total':
                title = 'All Faculty';
                data = allFaculty;
                break;
            default:
                return;
        }
        setModalTitle(title);
        setModalData(data);
        setShowDetailModal(true);
    };

    const fetchData = useCallback(async () => {
        if (!currentUser?.docId || !currentUser?.tenantId) {
            setError("Current user information is not fully loaded."); 
            setIsLoading(false); 
            return;
        }
        setIsLoading(true); 
        setError('');
        try {
            // Fetch tenant settings for academic year
            try {
                const tenantQuery = query(
                    collection(db, 'tenants'), 
                    where('tenantId', '==', currentUser.tenantId)
                );
                const tenantSnapshot = await getDocs(tenantQuery);
                if (!tenantSnapshot.empty) {
                    const tenantData = tenantSnapshot.docs[0].data();
                    setTenantSettings(tenantData);
                    if (tenantData.academicYearStart && tenantData.academicYearEnd) {
                        setStatsStartDate(tenantData.academicYearStart);
                        setStatsEndDate(tenantData.academicYearEnd);
                    }
                }
            } catch (tenantError) {
                console.warn("Could not fetch tenant settings:", tenantError);
            }

            const pendingQuery = query(
                collection(db, 'absence_requests'), 
                where('tenantId', '==', currentUser.tenantId), 
                where('supervisorId', '==', currentUser.docId), 
                where('status', '==', 'pending')
            );
            const pendingSnapshot = await getDocs(pendingQuery);
            setPendingRequests(pendingSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

            // Fetch direct reports
            const reportsQuery = query(
                collection(db, 'faculty_staff'), 
                where('tenantId', '==', currentUser.tenantId), 
                where('supervisorId', '==', currentUser.docId)
            );
            const reportsSnapshot = await getDocs(reportsQuery);
            
            // NEW: Store team members for the direct reports list
            const teamMembersList = reportsSnapshot.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            }));
            setTeamMembers(teamMembersList);
            console.log('📋 Direct reports found:', teamMembersList.length);
            
            const reportUids = reportsSnapshot.docs.map(d => d.data().uid).filter(uid => uid);

            if (reportUids.length > 0) {
                const teamAbsenceQuery = query(
                    collection(db, 'absence_requests'), 
                    where('tenantId', '==', currentUser.tenantId), 
                    where('requesterUid', 'in', reportUids.slice(0, 30))
                );
                const teamAbsenceSnapshots = await getDocs(teamAbsenceQuery);
                const absences = teamAbsenceSnapshots.docs.map(d => ({ id: d.id, ...d.data() }));
                setTeamAbsences(absences);
                setDeniedRequests(absences.filter(req => req.status === 'denied').sort((a,b) => 
                    parseISO(b.startDate || b.dateOfAbsence) - parseISO(a.startDate || a.dateOfAbsence)
                ));

                // Fetch substitute requests for off-campus statistics
                const subRequestQuery = query(
                    collection(db, 'sub_requests'),
                    where('tenantId', '==', currentUser.tenantId)
                );
                const subRequestSnapshot = await getDocs(subRequestQuery);
                const subRequests = subRequestSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                
                const offCampusFromSubRequests = subRequests.filter(req => {
                    if (!req.isOffCampusOnly) return false;
                    const requesterUid = req.requesterUid;
                    return reportUids.includes(requesterUid);
                });
                
                const offCampusFromAbsences = absences.filter(req => 
                    (req.isOffCampusOnly || req.isOffCampusNotification || req.requestType === 'offCampus') 
                    && req.status === 'approved'
                ).map(req => ({
                    ...req,
                    date: req.startDate,
                    periods: req.periods || [],
                    departureTime: req.departureTime,
                    returnTime: req.returnTime
                }));
                
                const allOffCampusRequests = [...offCampusFromSubRequests, ...offCampusFromAbsences];
                setOffCampusRequests(allOffCampusRequests);
            } else {
                setTeamAbsences([]); 
                setDeniedRequests([]);
                setOffCampusRequests([]);
            }
        } catch (err) {
            console.error("Error fetching supervisor data:", err); 
            setError("Failed to load dashboard data.");
        }
        setIsLoading(false);
    }, [currentUser]);

    const fetchTeamPTOBalances = useCallback(async () => {
        if (!currentUser?.tenantId) return;
        
        setIsLoadingPTO(true);
        try {
            const teamMemberUIDs = teamAbsences
                .map(req => req.requesterUid || req.employeeId)
                .filter((uid, index, self) => uid && self.indexOf(uid) === index);
            
            if (teamMemberUIDs.length === 0) {
                setTeamPTOBalances([]);
                setIsLoadingPTO(false);
                return;
            }
            
            const now = new Date();
            const currentYear = now.getFullYear();
            const ptoYearStart = now.getMonth() >= 6 ? currentYear : currentYear - 1;
            const ptoYear = `${ptoYearStart}-${ptoYearStart + 1}`;
            
            const ptoBalances = [];
            
            for (const uid of teamMemberUIDs) {
                const balanceDocId = `${uid}_${ptoYear}`;
                const balanceRef = doc(db, 'employee_pto_balances', balanceDocId);
                const balanceDoc = await getDoc(balanceRef);
                
                if (balanceDoc.exists()) {
                    const data = balanceDoc.data();
                    
                    const facultyQuery = query(
                        collection(db, 'faculty_staff'),
                        where('uid', '==', uid),
                        where('tenantId', '==', currentUser.tenantId)
                    );
                    const facultySnapshot = await getDocs(facultyQuery);
                    
                    let employeeName = 'Unknown';
                    let employeeEmail = '';
                    if (!facultySnapshot.empty) {
                        const facultyData = facultySnapshot.docs[0].data();
                        employeeName = facultyData.name || `${facultyData.firstName} ${facultyData.lastName}`;
                        employeeEmail = facultyData.email || '';
                    }

                    ptoBalances.push({
                        ...data,
                        employeeName,
                        employeeEmail,
                        remaining: (data.daysAllocated || 0) - (data.daysUsed || 0) - (data.daysPending || 0)
                    });
                }
            }
            
            ptoBalances.sort((a, b) => a.employeeName.localeCompare(b.employeeName));
            setTeamPTOBalances(ptoBalances);
        } catch (err) {
            console.error('Error fetching team PTO balances:', err);
        } finally {
            setIsLoadingPTO(false);
        }
    }, [currentUser, teamAbsences]);

    // Fetch Campus Attendance Data
    const fetchAttendanceData = async () => {
        if (!currentUser?.tenantId) return;
        
        setIsLoadingAttendance(true);
        try {
            const facultyQuery = query(
                collection(db, 'faculty_staff'),
                where('tenantId', '==', currentUser.tenantId)
            );
            const facultySnapshot = await getDocs(facultyQuery);
            const facultyList = facultySnapshot.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            })).filter(f => f.role !== 'Outside Sub');
            
            setAllFaculty(facultyList);

            const absenceQuery = query(
                collection(db, 'absence_requests'),
                where('tenantId', '==', currentUser.tenantId)
            );
            const absenceSnapshot = await getDocs(absenceQuery);
            const allAbsenceRequests = absenceSnapshot.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            }));
            
            const approvedAbsences = allAbsenceRequests.filter(req => {
                if (req.status === 'approved') return true;
                if ((req.isOffCampusOnly || req.isOffCampusNotification || req.requestType === 'offCampus')) {
                    return true;
                }
                return false;
            });

            const subRequestQuery = query(
                collection(db, 'sub_requests'),
                where('tenantId', '==', currentUser.tenantId)
            );
            const subRequestSnapshot = await getDocs(subRequestQuery);
            const subRequests = subRequestSnapshot.docs.map(d => ({ 
                id: d.id, 
                ...d.data() 
            }));

            const checkDate = parseISO(attendanceDate);
            const absentList = [];
            const offCampusList = [];
            const presentList = [...facultyList];

            approvedAbsences.forEach(absence => {
                if (absence.isOffCampusOnly || absence.isOffCampusNotification || absence.requestType === 'offCampus') {
                    if (absence.startDate && format(parseISO(absence.startDate), 'yyyy-MM-dd') === attendanceDate) {
                        const faculty = facultyList.find(f => f.uid === absence.requesterUid || f.email === absence.requesterEmail);
                        if (faculty && absence.departureTime && absence.returnTime) {
                            try {
                                const [checkHour, checkMin] = attendanceTime.split(':').map(Number);
                                const [depHour, depMin] = absence.departureTime.split(':').map(Number);
                                const [retHour, retMin] = absence.returnTime.split(':').map(Number);
                                
                                const checkMinutes = checkHour * 60 + checkMin;
                                const depMinutes = depHour * 60 + depMin;
                                const retMinutes = retHour * 60 + retMin;
                                
                                if (checkMinutes >= depMinutes && checkMinutes <= retMinutes) {
                                    offCampusList.push({
                                        ...faculty,
                                        reason: absence.reason === 'Other' ? absence.reasonOther : absence.reason,
                                        type: 'Off-Campus',
                                        timeRange: `${absence.departureTime} - ${absence.returnTime}`
                                    });
                                    const index = presentList.findIndex(f => f.id === faculty.id);
                                    if (index > -1) presentList.splice(index, 1);
                                }
                            } catch (e) {
                                console.error('Error parsing time for off-campus check:', e);
                            }
                        }
                    }
                    return;
                }

                if (absence.startDate && absence.endDate) {
                    const start = parseISO(absence.startDate);
                    const end = parseISO(absence.endDate);
                    if (isValid(start) && isValid(end) && checkDate >= start && checkDate <= end) {
                        const faculty = facultyList.find(f => f.uid === absence.requesterUid || f.email === absence.requesterEmail);
                        if (faculty) {
                            absentList.push({
                                ...faculty,
                                reason: absence.reason === 'Other' ? absence.reasonOther : absence.reason,
                                type: 'Absence Request'
                            });
                            const index = presentList.findIndex(f => f.id === faculty.id);
                            if (index > -1) presentList.splice(index, 1);
                        }
                    }
                } else if (absence.dateOfAbsence) {
                    const absenceDate = parseISO(absence.dateOfAbsence);
                    if (isValid(absenceDate) && format(absenceDate, 'yyyy-MM-dd') === attendanceDate) {
                        const faculty = facultyList.find(f => f.uid === absence.requesterUid || f.email === absence.requesterEmail);
                        if (faculty) {
                            absentList.push({
                                ...faculty,
                                reason: absence.reason === 'Other' ? absence.reasonOther : absence.reason,
                                type: 'Absence Request'
                            });
                            const index = presentList.findIndex(f => f.id === faculty.id);
                            if (index > -1) presentList.splice(index, 1);
                        }
                    }
                }
            });

            subRequests.forEach(subReq => {
                if (subReq.date === attendanceDate) {
                    const faculty = facultyList.find(f => 
                        f.uid === subReq.requesterUid || 
                        f.email === subReq.requesterEmail ||
                        f.name === subReq.name
                    );
                    if (faculty) {
                        if (subReq.isOffCampusOnly) {
                            offCampusList.push({
                                ...faculty,
                                reason: subReq.reason,
                                type: 'Off-Campus'
                            });
                        } else {
                            if (!absentList.find(a => a.id === faculty.id)) {
                                absentList.push({
                                    ...faculty,
                                    reason: subReq.reason,
                                    periods: subReq.periods,
                                    type: 'Substitute Request'
                                });
                            }
                        }
                        const index = presentList.findIndex(f => f.id === faculty.id);
                        if (index > -1) presentList.splice(index, 1);
                    }
                }
            });

            setAbsentFaculty(absentList);
            setOffCampusFaculty(offCampusList);
            setPresentFaculty(presentList);
        } catch (err) {
            console.error("Error fetching attendance data:", err);
            setError("Failed to load attendance data.");
        }
        setIsLoadingAttendance(false);
    };

    useEffect(() => {
        if (currentUser && currentUser.tenantId) {
            fetchData();
        }
    }, [currentUser, fetchData]);

    useEffect(() => {
        if (teamAbsences.length > 0) {
            fetchTeamPTOBalances();
        }
    }, [teamAbsences.length]);

    useEffect(() => {
        if (showAttendance) {
            fetchAttendanceData();
        }
    }, [showAttendance, attendanceDate, attendanceTime]);

    const togglePTOOverride = (requestId) => {
        setPtoOverrides(prev => ({
            ...prev,
            [requestId]: !prev[requestId]
        }));
    };

    const handleRequestUpdate = async (requestId, newStatus) => {
        let denialReason = null;
        if (newStatus === 'denied') {
            denialReason = prompt("Please provide a brief reason for the denial (optional):");
            if (denialReason === null) { return; }
        } else if (!window.confirm(`Are you sure you want to ${newStatus} this request?`)) {
            return;
        }
        
        try {
            const requestDocRef = doc(db, 'absence_requests', requestId);
            const requestDoc = await getDoc(requestDocRef);
            if (!requestDoc.exists()) {
                setError('Request not found');
                return;
            }
            const requestData = requestDoc.data();
            const oldStatus = requestData.status;
            const shouldCountPTO = !ptoOverrides[requestId];
            
            const updatePayload = {
                status: newStatus,
                countsPTO: shouldCountPTO,
                history: arrayUnion({
                    status: newStatus,
                    updatedAt: new Date(),
                    updatedBy: currentUser.name,
                    reason: newStatus === 'denied' ? denialReason : '',
                    ptoOverride: !shouldCountPTO
                })
            };
            
            if (newStatus === 'denied') {
                updatePayload.denialReason = denialReason;
            }
            
            await updateDoc(requestDocRef, updatePayload);
            
            if (shouldCountPTO) {
                try {
                    await updatePTOForAbsenceChange(
                        requestData.requesterUid || requestData.employeeId,
                        currentUser.tenantId,
                        requestData.days || 1,
                        oldStatus,
                        newStatus
                    );
                    console.log('✅ PTO balance updated successfully');
                } catch (ptoError) {
                    console.error('❌ Failed to update PTO balance:', ptoError);
                }
            } else {
                console.log('ℹ️ PTO override active - balance not updated');
            }
            
            setPtoOverrides(prev => {
                const newState = { ...prev };
                delete newState[requestId];
                return newState;
            });
            
            fetchData();
        } catch (err) {
            console.error(`Error ${newStatus}ing request:`, err);
            setError(`Failed to ${newStatus} the request.`);
        }
    };
    
    const formatDateDisplay = (req) => {
        if (req.startDate) {
            const start = parseISO(req.startDate);
            const end = parseISO(req.endDate || req.startDate);
            if (!isValid(start)) return 'Invalid Date';
            const startFormatted = format(start, 'MM/dd/yyyy');
            if (format(start, 'yyyyMMdd') === format(end, 'yyyyMMdd')) {
                return startFormatted;
            }
            return `${startFormatted} - ${format(end, 'MM/dd/yyyy')}`;
        }
        if (req.dateOfAbsence) {
            const date = parseISO(req.dateOfAbsence);
            return isValid(date) ? format(date, 'MM/dd/yyyy') : 'Invalid Date';
        }
        return 'No Date Provided';
    };

    // Calculate team statistics with time frame filter
    const calculateTeamStats = () => {
        const stats = {};
        const startDate = parseISO(statsStartDate);
        const endDate = parseISO(statsEndDate);

        teamAbsences.forEach(request => {
            if (request.status !== 'approved') return;
            if (request.isOffCampusOnly || request.isOffCampusNotification || request.requestType === 'offCampus') {
                return;
            }
            
            const name = request.requesterName;
            if (!stats[name]) { 
                stats[name] = { 
                    totalDays: 0, 
                    halfDayCount: 0,
                    byReason: {}, 
                    offCampusCount: 0, 
                    offCampusHours: 0,
                    offCampusDetails: []
                }; 
            }
            
            let absenceStart, absenceEnd;
            if (request.startDate && request.endDate) {
                absenceStart = parseISO(request.startDate);
                absenceEnd = parseISO(request.endDate);
            } else if (request.dateOfAbsence) {
                absenceStart = parseISO(request.dateOfAbsence);
                absenceEnd = absenceStart;
            } else {
                return;
            }

            if (!isValid(absenceStart) || !isValid(absenceEnd)) return;
            if (absenceEnd < startDate || absenceStart > endDate) return;
            
            let days;
            if (request.isHalfDay) {
                days = 0.5;
                stats[name].halfDayCount += 1;
            } else {
                days = differenceInBusinessDays(absenceEnd, absenceStart) + 1;
            }
            stats[name].totalDays += days;
            const reason = request.reason === 'Other' ? (request.reasonOther || 'Other') : request.reason;
            stats[name].byReason[reason] = (stats[name].byReason[reason] || 0) + days;
        });

        offCampusRequests.forEach(request => {
            const dateString = request.date || request.startDate;
            if (!dateString) return;
            
            const requestDate = parseISO(dateString);
            if (!isValid(requestDate) || requestDate < startDate || requestDate > endDate) return;

            const name = request.name || request.requesterName;
            if (!name) return;
            
            if (!stats[name]) { 
                stats[name] = { 
                    totalDays: 0,
                    halfDayCount: 0, 
                    byReason: {}, 
                    offCampusCount: 0, 
                    offCampusHours: 0,
                    offCampusDetails: []
                }; 
            }

            stats[name].offCampusCount += 1;
            
            let hours = 0;
            let timeInfo = '';
            
            if (request.departureTime && request.returnTime) {
                try {
                    const [depHour, depMin] = request.departureTime.split(':').map(Number);
                    const [retHour, retMin] = request.returnTime.split(':').map(Number);
                    
                    if (!isNaN(depHour) && !isNaN(depMin) && !isNaN(retHour) && !isNaN(retMin)) {
                        const depMinutes = depHour * 60 + depMin;
                        const retMinutes = retHour * 60 + retMin;
                        hours = Math.max(0, (retMinutes - depMinutes) / 60);
                        timeInfo = `${request.departureTime} - ${request.returnTime}`;
                    } else {
                        hours = 8;
                        timeInfo = 'Time format error';
                    }
                } catch (e) {
                    hours = 8;
                    timeInfo = 'Time format error';
                }
            } else if (request.periods && request.periods.length > 0) {
                hours = request.periods.length * 0.75;
                timeInfo = `Periods: ${request.periods.join(', ')}`;
            } else {
                hours = 8;
                timeInfo = 'All day';
            }
            
            stats[name].offCampusHours += hours;
            stats[name].offCampusDetails.push({
                date: format(requestDate, 'MM/dd/yyyy'),
                time: timeInfo,
                reason: request.reason || 'Not specified',
                hours: hours
            });
        });

        return stats;
    };

    const teamStats = calculateTeamStats();

    // Campus Attendance View
    if (showAttendance) {
        return (
            <div style={styles.dashboardContainer}>
                <div style={styles.attendanceHeader}>
                    <h1 style={styles.h1}>🏫 Campus Attendance Tracker</h1>
                    <p style={styles.dashboardSubtitle}>
                        Real-time campus roster and attendance monitoring
                    </p>
                </div>

                <div style={styles.dashboardCard}>
                    <div style={styles.cardHeader}>
                        <h2 style={styles.h2}>📅 Select Date & Time</h2>
                        <button 
                            onClick={() => setShowAttendance(false)}
                            style={{...styles.btn, ...styles.btnSecondary, ...styles.btnSm}}
                        >
                            ← Back to Dashboard
                        </button>
                    </div>
                    <div style={styles.controlPanel}>
                        <div style={styles.dateTimeControls}>
                            <div style={styles.controlGroup}>
                                <label style={styles.controlLabel}>Date:</label>
                                <input 
                                    type="date" 
                                    value={attendanceDate}
                                    onChange={(e) => setAttendanceDate(e.target.value)}
                                    style={styles.dateTimeInput}
                                />
                            </div>
                            <div style={styles.controlGroup}>
                                <label style={styles.controlLabel}>Time:</label>
                                <input 
                                    type="time" 
                                    value={attendanceTime}
                                    onChange={(e) => setAttendanceTime(e.target.value)}
                                    style={styles.dateTimeInput}
                                />
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignSelf: 'flex-end', marginBottom: '2px' }}>
                                <button 
                                    onClick={() => {
                                        setAttendanceDate(format(new Date(), 'yyyy-MM-dd'));
                                        setAttendanceTime(format(new Date(), 'HH:mm'));
                                        setTimeout(() => fetchAttendanceData(), 100);
                                    }}
                                    style={{...styles.btn, ...styles.btnPrimary}}
                                >
                                    Set to Now
                                </button>
                                <button 
                                    onClick={() => fetchAttendanceData()}
                                    disabled={isLoadingAttendance}
                                    style={{
                                        ...styles.btn, 
                                        ...styles.btnSuccess,
                                        opacity: isLoadingAttendance ? 0.6 : 1,
                                        cursor: isLoadingAttendance ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isLoadingAttendance ? 'Loading...' : '🔄 Refresh'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={styles.dashboardCard}>
                    <div style={styles.cardHeader}>
                        <h2 style={styles.h2}>📊 Attendance Summary</h2>
                        <span style={{...styles.badge, ...styles.badgeInfo}}>
                            {format(parseISO(attendanceDate), 'MMM dd, yyyy')} at {attendanceTime}
                        </span>
                    </div>
                    <div style={styles.attendanceSummaryContainer}>
                        <p style={{ textAlign: 'center', color: '#6c757d', fontSize: '0.85rem', marginBottom: '15px', fontStyle: 'italic' }}>
                            Click any card below for details
                        </p>
                        <div style={styles.statsGrid}>
                            <div 
                                style={{...styles.statCard, ...styles.presentCard}}
                                onClick={() => handleCardClick('present')}
                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={styles.statIcon}>✅</div>
                                <div style={{...styles.statNumber, ...styles.presentCardNumber}}>{presentFaculty.length}</div>
                                <div style={styles.statLabel}>On Campus</div>
                            </div>
                            <div 
                                style={{...styles.statCard, ...styles.absentCard}}
                                onClick={() => handleCardClick('absent')}
                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={styles.statIcon}>🚫</div>
                                <div style={{...styles.statNumber, ...styles.absentCardNumber}}>{absentFaculty.length}</div>
                                <div style={styles.statLabel}>Absent</div>
                            </div>
                            <div 
                                style={{...styles.statCard, ...styles.offcampusCard}}
                                onClick={() => handleCardClick('offcampus')}
                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={styles.statIcon}>🚗</div>
                                <div style={{...styles.statNumber, ...styles.offcampusCardNumber}}>{offCampusFaculty.length}</div>
                                <div style={styles.statLabel}>Off-Campus</div>
                            </div>
                            <div 
                                style={{...styles.statCard, ...styles.totalCard}}
                                onClick={() => handleCardClick('total')}
                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={styles.statIcon}>👥</div>
                                <div style={{...styles.statNumber, ...styles.totalCardNumber}}>{allFaculty.length}</div>
                                <div style={styles.statLabel}>Total Faculty</div>
                            </div>
                        </div>
                    </div>
                </div>

                {absentFaculty.length > 0 && (
                    <div style={styles.dashboardCard}>
                        <div style={styles.cardHeader}>
                            <h2 style={styles.h2}>🚫 Absent Faculty</h2>
                            <span style={{...styles.badge, ...styles.badgeDanger}}>
                                {absentFaculty.length} absent
                            </span>
                        </div>
                        <div style={styles.tableContainer}>
                            <table style={styles.modernTable}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Name</th>
                                        <th style={styles.th}>Department</th>
                                        <th style={styles.th}>Reason</th>
                                        <th style={styles.th}>Type</th>
                                        <th style={styles.th}>Periods</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {absentFaculty.map(faculty => (
                                        <tr key={faculty.id}>
                                            <td style={styles.td}>
                                                <div style={styles.employeeCell}>
                                                    <strong>{faculty.name || faculty.displayName}</strong>
                                                </div>
                                            </td>
                                            <td style={styles.td}>{faculty.department || 'N/A'}</td>
                                            <td style={styles.td}>
                                                <span style={styles.reasonTag}>
                                                    {faculty.reason || 'Not specified'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={styles.typeBadge}>
                                                    {faculty.type}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{faculty.periods ? faculty.periods.join(', ') : 'All day'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {offCampusFaculty.length > 0 && (
                    <div style={styles.dashboardCard}>
                        <div style={styles.cardHeader}>
                            <h2 style={styles.h2}>🚗 Off-Campus Faculty</h2>
                            <span style={{...styles.badge, ...styles.badgeWarning}}>
                                {offCampusFaculty.length} off-campus
                            </span>
                        </div>
                        <div style={styles.tableContainer}>
                            <table style={styles.modernTable}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Name</th>
                                        <th style={styles.th}>Department</th>
                                        <th style={styles.th}>Reason</th>
                                        <th style={styles.th}>Time Range</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {offCampusFaculty.map(faculty => (
                                        <tr key={faculty.id}>
                                            <td style={styles.td}>
                                                <div style={styles.employeeCell}>
                                                    <strong>{faculty.name || faculty.displayName}</strong>
                                                </div>
                                            </td>
                                            <td style={styles.td}>{faculty.department || 'N/A'}</td>
                                            <td style={styles.td}>
                                                <span style={styles.reasonTag}>
                                                    {faculty.reason || 'Not specified'}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{faculty.timeRange || 'N/A'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                <div style={styles.dashboardCard}>
                    <div style={styles.cardHeader}>
                        <h2 style={styles.h2}>✅ On-Campus Faculty</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{...styles.badge, ...styles.badgeSuccess}}>
                                {presentFaculty.length} present
                            </span>
                            <button 
                                onClick={() => setShowFullRoster(!showFullRoster)}
                                style={{...styles.btn, ...styles.btnPrimary, ...styles.btnSm}}
                            >
                                {showFullRoster ? 'Hide' : 'Show'} Full Roster
                            </button>
                        </div>
                    </div>
                    {showFullRoster && (
                        <div style={styles.tableContainer}>
                            <table style={styles.modernTable}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>Name</th>
                                        <th style={styles.th}>Department</th>
                                        <th style={styles.th}>Role</th>
                                        <th style={styles.th}>Present at Selected Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {presentFaculty.map(faculty => (
                                        <tr key={faculty.id}>
                                            <td style={styles.td}>
                                                <div style={styles.employeeCell}>
                                                    <strong>{faculty.name || faculty.displayName}</strong>
                                                </div>
                                            </td>
                                            <td style={styles.td}>{faculty.department || 'N/A'}</td>
                                            <td style={styles.td}>{faculty.role || 'Faculty'}</td>
                                            <td style={styles.td}>
                                                <span style={{...styles.badge, ...styles.badgeSuccess}}>
                                                    ✓ Present
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                
                {showDetailModal && (
                    <>
                        <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)} />
                        <div style={styles.detailModal}>
                            <div style={styles.modalHeader}>
                                <h3 style={styles.modalTitle}>{modalTitle}</h3>
                                <button 
                                    style={styles.closeModalButton}
                                    onClick={() => setShowDetailModal(false)}
                                >
                                    ×
                                </button>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'center' }}>
                                <button 
                                    onClick={() => exportToCSV()} 
                                    style={{ backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}
                                >
                                    📥 Export CSV
                                </button>
                                <button 
                                    onClick={() => printReport()} 
                                    style={{ backgroundColor: '#17a2b8', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}
                                >
                                    🖨️ Print Report
                                </button>
                            </div>
                            <div>
                                {modalData.length > 0 ? (
                                    modalData.map((faculty, index) => (
                                        <div key={index} style={styles.facultyListItem}>
                                            <div>
                                                <div style={styles.facultyName}>{faculty.name || faculty.displayName}</div>
                                                <div style={styles.facultyEmail}>{faculty.email}</div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={styles.noDataMessage}>
                                        No faculty members in this category
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        );
    }

    // Main Dashboard View
    if (isLoading) {
        return (
            <div style={styles.dashboardContainer}>
                <div style={styles.loadingSpinner}>
                    <div style={styles.spinner}></div>
                    <p>Loading Supervisor Dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.dashboardContainer}>
                <div style={styles.errorMessage}>
                    <h2>⚠️ Error</h2>
                    <p>{error}</p>
                    <button style={{...styles.btn, ...styles.btnPrimary}} onClick={fetchData}>
                        Try Again
                    </button>
                </div>
            </div>
        );
    }
    
    return (
        <div style={styles.dashboardContainer}>
            <div style={styles.dashboardHeader}>
                <h1 style={styles.h1}>👥 Supervisor Dashboard</h1>
                <p style={styles.dashboardSubtitle}>
                    Manage your team's absence requests and view team statistics
                </p>
            </div>

            <div style={styles.actionBar}>
                <button 
                    onClick={() => setShowAttendance(true)}
                    style={styles.btnAttendance}
                >
                    🏫 Campus Attendance Tracker
                </button>
            </div>

            {/* NEW: Direct Reports Card - Always show first */}
            <div style={styles.dashboardCard}>
                <div style={styles.cardHeader}>
                    <h2 style={styles.h2}>👥 My Direct Reports</h2>
                    <span style={{...styles.badge, ...styles.badgePrimary}}>
                        {teamMembers.length} team member{teamMembers.length !== 1 ? 's' : ''}
                    </span>
                </div>
                
                {teamMembers.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>👤</div>
                        <h3 style={styles.emptyStateH3}>No direct reports found</h3>
                        <p style={styles.emptyStateP}>You don't have any team members assigned to you yet.</p>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.modernTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Name</th>
                                    <th style={styles.th}>Email</th>
                                    <th style={styles.th}>Department</th>
                                    <th style={styles.th}>Role</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamMembers.map(member => (
                                    <tr key={member.id}>
                                        <td style={styles.td}>
                                            <div style={styles.employeeCell}>
                                                <strong>{member.name || member.displayName || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'N/A'}</strong>
                                            </div>
                                        </td>
                                        <td style={styles.td}>{member.email || 'N/A'}</td>
                                        <td style={styles.td}>{member.department || 'N/A'}</td>
                                        <td style={styles.td}>{member.role || 'Faculty/Staff'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Pending Requests Card */}
            <div style={styles.dashboardCard}>
                <div style={styles.cardHeader}>
                    <h2 style={styles.h2}>🔔 Pending Absence Approvals</h2>
                    <span style={{...styles.badge, ...styles.badgeWarning}}>
                        {pendingRequests.length} pending
                    </span>
                </div>
                
                {pendingRequests.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>✅</div>
                        <h3 style={styles.emptyStateH3}>All caught up!</h3>
                        <p style={styles.emptyStateP}>You have no pending requests to review.</p>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.modernTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Employee</th>
                                    <th style={styles.th}>Date(s)</th>
                                    <th style={styles.th}>Reason</th>
                                    <th style={styles.th}>Notes</th>
                                    <th style={styles.th}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pendingRequests.map(req => (
                                    <tr key={req.id}>
                                        <td style={styles.td}>
                                            <div style={styles.employeeCell}>
                                                <strong>{req.requesterName}</strong>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.dateDisplay}>
                                                {formatDateDisplay(req)}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.reasonTag}>
                                                {req.reason === 'Other' ? req.reasonOther : req.reason}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.notesCell}>
                                                {req.notes || <em>No notes</em>}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={{ 
                                                marginBottom: '10px',
                                                padding: '8px',
                                                backgroundColor: '#fff3cd',
                                                borderRadius: '6px',
                                                border: '1px solid #ffc107'
                                            }}>
                                                <label style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    color: '#856404'
                                                }}>
                                                    <input 
                                                        type="checkbox"
                                                        checked={ptoOverrides[req.id] || false}
                                                        onChange={() => togglePTOOverride(req.id)}
                                                        style={{
                                                            cursor: 'pointer',
                                                            width: '16px',
                                                            height: '16px'
                                                        }}
                                                    />
                                                    <span>Don't count against PTO</span>
                                                </label>
                                            </div>
                                            
                                            <div style={styles.actionButtons}>
                                                <button 
                                                    onClick={() => handleRequestUpdate(req.id, 'approved')}
                                                    style={{...styles.btn, ...styles.btnSuccess, ...styles.btnSm}}
                                                >
                                                    ✓ Approve
                                                </button>
                                                <button 
                                                    onClick={() => handleRequestUpdate(req.id, 'denied')}
                                                    style={{...styles.btn, ...styles.btnDanger, ...styles.btnSm}}
                                                >
                                                    ✗ Deny
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Team PTO Balances Card */}
            <div style={styles.dashboardCard}>
                <div style={styles.cardHeader}>
                    <h2 style={styles.h2}>💼 Team PTO Balances</h2>
                    <span style={{...styles.badge, ...styles.badgeInfo}}>
                        2025-2026 School Year
                    </span>
                </div>
                
                {isLoadingPTO ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6c757d' }}>
                        Loading PTO data...
                    </div>
                ) : teamPTOBalances.length === 0 ? (
                    <div style={styles.noDataMessage}>
                        <p>No PTO data available for your team members.</p>
                        <p style={{ fontSize: '14px', marginTop: '10px' }}>
                            PTO balances are created automatically when employees are added.
                        </p>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.modernTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Employee</th>
                                    <th style={{...styles.th, textAlign: 'center'}}>Allocated</th>
                                    <th style={{...styles.th, textAlign: 'center'}}>Used</th>
                                    <th style={{...styles.th, textAlign: 'center'}}>Pending</th>
                                    <th style={{...styles.th, textAlign: 'center'}}>Remaining</th>
                                    <th style={{...styles.th, textAlign: 'center'}}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {teamPTOBalances.map((balance, index) => {
                                    const remaining = balance.remaining;
                                    const percentUsed = balance.daysAllocated > 0 
                                        ? ((balance.daysUsed / balance.daysAllocated) * 100).toFixed(0)
                                        : 0;
                                    
                                    let statusColor = '#28a745';
                                    let statusText = 'Good';
                                    if (remaining < 0) {
                                        statusColor = '#dc3545';
                                        statusText = 'Overdrawn';
                                    } else if (remaining < 3) {
                                        statusColor = '#ffc107';
                                        statusText = 'Low';
                                    }
                                    
                                    return (
                                        <tr key={index}>
                                            <td style={styles.td}>
                                                <div>
                                                    <strong>{balance.employeeName}</strong>
                                                    {balance.employeeEmail && (
                                                        <div style={{
                                                            fontSize: '13px',
                                                            color: '#6c757d',
                                                            marginTop: '2px'
                                                        }}>
                                                            {balance.employeeEmail}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{...styles.td, textAlign: 'center'}}>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontWeight: '600'
                                                }}>
                                                    {balance.daysAllocated || 0}
                                                </span>
                                            </td>
                                            <td style={{...styles.td, textAlign: 'center'}}>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    backgroundColor: '#ffebee',
                                                    color: '#c62828',
                                                    fontWeight: '600'
                                                }}>
                                                    {balance.daysUsed || 0}
                                                </span>
                                            </td>
                                            <td style={{...styles.td, textAlign: 'center'}}>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    backgroundColor: '#fff3e0',
                                                    color: '#e65100',
                                                    fontWeight: '600'
                                                }}>
                                                    {balance.daysPending || 0}
                                                </span>
                                            </td>
                                            <td style={{...styles.td, textAlign: 'center'}}>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    borderRadius: '12px',
                                                    backgroundColor: remaining >= 0 ? '#e8f5e9' : '#ffebee',
                                                    color: remaining >= 0 ? '#2e7d32' : '#c62828',
                                                    fontWeight: '700',
                                                    fontSize: '16px'
                                                }}>
                                                    {remaining}
                                                </span>
                                            </td>
                                            <td style={{...styles.td, textAlign: 'center'}}>
                                                <div style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <span style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        backgroundColor: statusColor,
                                                        color: 'white',
                                                        fontWeight: '600',
                                                        fontSize: '13px'
                                                    }}>
                                                        {statusText}
                                                    </span>
                                                    <span style={{
                                                        fontSize: '11px',
                                                        color: '#6c757d'
                                                    }}>
                                                        {percentUsed}% used
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            
                {teamPTOBalances.length > 0 && (
                    <div style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '15px'
                    }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#1976d2' }}>
                                {teamPTOBalances.reduce((sum, b) => sum + (b.daysAllocated || 0), 0)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                Total Allocated
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#c62828' }}>
                                {teamPTOBalances.reduce((sum, b) => sum + (b.daysUsed || 0), 0)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                Total Used
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#e65100' }}>
                                {teamPTOBalances.reduce((sum, b) => sum + (b.daysPending || 0), 0)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                Total Pending
                            </div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '24px', fontWeight: '700', color: '#2e7d32' }}>
                                {teamPTOBalances.reduce((sum, b) => sum + b.remaining, 0)}
                            </div>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                Total Remaining
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Team Statistics Card */}
            <div style={styles.dashboardCard}>
                <div style={styles.cardHeader}>
                    <h2 style={styles.h2}>📊 Direct Report Statistics</h2>
                    <span style={{...styles.badge, ...styles.badgeInfo}}>
                        {(statsStartDate && statsEndDate) ? 
                            `${format(parseISO(statsStartDate), 'MMM d, yyyy')} - ${format(parseISO(statsEndDate), 'MMM d, yyyy')}` :
                            'Select date range'
                        }
                    </span>
                </div>
                
                <div style={styles.controlPanel}>
                    <div style={styles.statsTimeFrame}>
                        <div style={styles.controlGroup}>
                            <label style={styles.controlLabel}>From:</label>
                            <input 
                                type="date" 
                                value={statsStartDate}
                                onChange={(e) => setStatsStartDate(e.target.value)}
                                style={styles.dateTimeInput}
                            />
                        </div>
                        <div style={styles.controlGroup}>
                            <label style={styles.controlLabel}>To:</label>
                            <input 
                                type="date" 
                                value={statsEndDate}
                                onChange={(e) => setStatsEndDate(e.target.value)}
                                style={styles.dateTimeInput}
                            />
                        </div>
                        <button 
                            onClick={() => {
                                if (tenantSettings?.academicYearStart && tenantSettings?.academicYearEnd) {
                                    setStatsStartDate(tenantSettings.academicYearStart);
                                    setStatsEndDate(tenantSettings.academicYearEnd);
                                } else {
                                    setStatsStartDate(getDefaultSchoolYearStart());
                                    setStatsEndDate(getDefaultSchoolYearEnd());
                                }
                            }}
                            style={{...styles.btn, ...styles.btnPrimary, backgroundColor: '#28a745'}}
                        >
                            School Year
                        </button>
                        <button 
                            onClick={() => {
                                setStatsStartDate(format(new Date(new Date().getFullYear(), 0, 1), 'yyyy-MM-dd'));
                                setStatsEndDate(format(new Date(), 'yyyy-MM-dd'));
                            }}
                            style={{...styles.btn, ...styles.btnPrimary}}
                        >
                            Year to Date
                        </button>
                        <button 
                            onClick={() => {
                                setStatsStartDate(format(startOfMonth(new Date()), 'yyyy-MM-dd'));
                                setStatsEndDate(format(endOfMonth(new Date()), 'yyyy-MM-dd'));
                            }}
                            style={{...styles.btn, ...styles.btnPrimary}}
                        >
                            This Month
                        </button>
                        <button 
                            onClick={() => {
                                setStatsStartDate(format(startOfMonth(subMonths(new Date(), 2)), 'yyyy-MM-dd'));
                                setStatsEndDate(format(new Date(), 'yyyy-MM-dd'));
                            }}
                            style={{...styles.btn, ...styles.btnPrimary}}
                        >
                            Last 3 Months
                        </button>
                    </div>
                </div>
                
                {Object.keys(teamStats).length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>📈</div>
                        <h3 style={styles.emptyStateH3}>No data available</h3>
                        <p style={styles.emptyStateP}>No absence or off-campus data found for your direct reports in this time frame.</p>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.modernTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Employee</th>
                                    <th style={styles.th}>Days Absent</th>
                                    <th style={styles.th}>Absence Breakdown</th>
                                    <th style={styles.th}>Off-Campus Stats</th>
                                    <th style={styles.th}>Off-Campus Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(teamStats).map(([name, stats]) => (
                                    <tr key={name}>
                                        <td style={styles.td}>
                                            <div style={styles.employeeCell}>
                                                <strong>{name}</strong>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.statsCell}>
                                                <span style={styles.totalDays}>
                                                    {stats.totalDays % 1 === 0 ? stats.totalDays : stats.totalDays.toFixed(1)}
                                                </span>
                                                <small style={styles.totalDaysSmall}>days</small>
                                                {stats.halfDayCount > 0 && (
                                                    <>
                                                        <br />
                                                        <span style={{ fontSize: '1rem', fontWeight: '600', color: '#856404' }}>
                                                            {stats.halfDayCount}
                                                        </span>
                                                        <small style={styles.totalDaysSmall}>half-days</small>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.breakdownCell}>
                                                {Object.entries(stats.byReason).map(([reason, count]) => (
                                                    <div key={reason} style={styles.reasonBreakdown}>
                                                        <span style={styles.reasonLabel}>{reason}:</span>
                                                        <span style={styles.reasonCount}>{count} day(s)</span>
                                                    </div>
                                                ))}
                                                {Object.keys(stats.byReason).length === 0 && (
                                                    <em style={{ color: '#6c757d' }}>No absences</em>
                                                )}
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.statsCell}>
                                                <span style={{...styles.totalDays, color: '#ffc107'}}>{stats.offCampusCount}</span>
                                                <small style={styles.totalDaysSmall}>instances</small>
                                                <br />
                                                <span style={{ fontSize: '1.2rem', fontWeight: '600', color: '#856404' }}>
                                                    {stats.offCampusHours.toFixed(1)}
                                                </span>
                                                <small style={styles.totalDaysSmall}>hours</small>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            {stats.offCampusDetails.length > 0 ? (
                                                <div style={styles.offCampusList}>
                                                    {stats.offCampusDetails.map((detail, idx) => (
                                                        <div key={idx} style={styles.offCampusItem}>
                                                            <div>
                                                                <span style={styles.offCampusDate}>{detail.date}</span>
                                                                <br />
                                                                <span style={styles.offCampusTime}>{detail.time}</span>
                                                            </div>
                                                            <div style={{ textAlign: 'right' }}>
                                                                <span style={styles.offCampusReason}>{detail.reason}</span>
                                                                <br />
                                                                <span style={styles.offCampusTime}>{detail.hours}h</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <em style={{ color: '#6c757d' }}>No off-campus time</em>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Denied Requests Card */}
            <div style={styles.dashboardCard}>
                <div style={styles.cardHeader}>
                    <h2 style={styles.h2}>❌ Denied Requests</h2>
                    <span style={{...styles.badge, ...styles.badgeSecondary}}>
                        {deniedRequests.length} denied
                    </span>
                </div>
                
                {deniedRequests.length === 0 ? (
                    <div style={styles.emptyState}>
                        <div style={styles.emptyIcon}>👍</div>
                        <h3 style={styles.emptyStateH3}>No denied requests</h3>
                        <p style={styles.emptyStateP}>No requests have been denied recently.</p>
                    </div>
                ) : (
                    <div style={styles.tableContainer}>
                        <table style={styles.modernTable}>
                            <thead>
                                <tr>
                                    <th style={styles.th}>Employee</th>
                                    <th style={styles.th}>Date(s)</th>
                                    <th style={styles.th}>Request Reason</th>
                                    <th style={styles.th}>Reason for Denial</th>
                                </tr>
                            </thead>
                            <tbody>
                                {deniedRequests.map(req => (
                                    <tr key={req.id} style={styles.deniedRow}>
                                        <td style={styles.td}>
                                            <div style={styles.employeeCell}>
                                                <strong>{req.requesterName}</strong>
                                            </div>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.dateDisplay}>
                                                {formatDateDisplay(req)}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <span style={styles.reasonTag}>
                                                {req.reason === 'Other' ? req.reasonOther : req.reason}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <div style={styles.denialReason}>
                                                <em>{req.denialReason || 'No reason provided.'}</em>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SupervisorDashboard;